import React from 'react';
import {
  Assignment,
  Assessment,
  Article,
  Business,
  People,
  WhatsApp,
  Settings as SettingsIcon,
  Security,
  Logout as LogoutIcon,
  MenuBook as MenuBookIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  TipsAndUpdates,
  Engineering,
  Checklist as ChecklistMuiIcon,
  Inventory as InventoryIcon,
  Home as HomeIcon,
  QrCode as QrCodeIcon,
  Repeat as RepeatIcon,
  Mail as MailIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material';
import { ROUTES } from '@/constants';

type Permission = keyof import('../contexts/AuthContext').IUser['permissions'];

export interface PageConfig {
  path: string;
  label: string;
  iconName: string;
  /** Um array exige QUALQUER UMA das permissões (OR) — ver mesmo suporte em ProtectedRoute.tsx.
   * Usado por Painel Público desde que passou a aceitar `clients` OU `suggestions` (pedido do
   * usuário, 14/09: unificação com as antigas páginas Sugestões/Caixa de Sugestões). */
  permission?: Permission | Permission[];
  component?: React.ComponentType<Record<string, unknown>>;
  requiresImplementation?: boolean;
  /** Pedido do usuário (04/09): divisão clara no menu entre páginas vinculadas a um condomínio
   * ('client') e páginas gerais do tenant ('global') — ver App.tsx (dropdown do menu) e
   * MENU_ORDER abaixo, que agrupa os dois blocos contiguamente. */
  scope: 'client' | 'global';
}

// Mapeamento de ícones
export const ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  Assignment,
  Assessment,
  Article,
  Business,
  People,
  WhatsApp,
  Settings: SettingsIcon,
  Security,
  Receipt: ReceiptIcon,
  AccountBalance: AccountBalanceIcon,
  Logout: LogoutIcon,
  MenuBook: MenuBookIcon,
  TipsAndUpdates,
  Engineering,
  Checklist: ChecklistMuiIcon,
  Inventory: InventoryIcon,
  Home: HomeIcon,
  QrCode: QrCodeIcon,
  Repeat: RepeatIcon,
  Mail: MailIcon,
  Insights: InsightsIcon
};

export const PAGES_CONFIG: Record<string, PageConfig> = {
  demands: {
    // Pedido do usuário (04/09): rótulo/ícone viram "Início" — a rota e o conteúdo (o board de
    // demandas) continuam os mesmos, ela já é a tela de pouso de todo usuário logado
    // (ROUTES.APP_DEMANDS === '/app'), equivalente ao "Início" que o mobile já tem como conceito
    // próprio.
    path: ROUTES.APP_DEMANDS,
    label: 'Início',
    iconName: 'Home',
    permission: 'demands',
    scope: 'client'
  },
  reports: {
    path: ROUTES.APP_REPORTS,
    label: 'Relatórios',
    iconName: 'Assessment',
    permission: 'reports',
    requiresImplementation: true,
    scope: 'client'
  },
  checklists: {
    path: ROUTES.APP_CHECKLISTS,
    label: 'Checklists',
    iconName: 'Checklist',
    permission: 'checklists',
    scope: 'client'
  },
  patrimony: {
    path: ROUTES.APP_PATRIMONY,
    label: 'Patrimônio',
    iconName: 'Inventory',
    permission: 'patrimony',
    scope: 'client'
  },
  // Pedido do usuário (04/09): QR Codes, Demandas Recorrentes, Caixa de Sugestões e Painel
  // Público são recursos do condomínio (não do cadastro de condomínios em si) — mesma permissão
  // implícita de hoje (sempre fizeram parte do acesso a Condomínios; não existe permissão
  // dedicada pra cada um separadamente).
  qrCodes: {
    path: ROUTES.APP_QR_CODES,
    label: 'QR Codes',
    iconName: 'QrCode',
    permission: 'clients',
    scope: 'client'
  },
  recurringDemands: {
    // Pedido do usuário (04/09): mesmo ícone já usado como indicador de "demanda recorrente" nos
    // cards do board (DemandCard.tsx) — consistência visual entre o menu e o indicador.
    path: ROUTES.APP_RECURRING_DEMANDS,
    label: 'Demandas Recorrentes',
    iconName: 'Repeat',
    permission: 'clients',
    scope: 'client'
  },
  publicPanel: {
    // Pedido do usuário (14/09): "Sugestões" e "Caixa de Sugestões" saem do menu como páginas
    // próprias — viram a aba "Sugestões" desta página (PublicPanelManagement.tsx). Aceita
    // `clients` OU `suggestions` (ver ProtectedRoute/routes.tsx) pra não tirar acesso de quem já
    // tinha só uma das duas.
    path: ROUTES.APP_PUBLIC_PANEL,
    label: 'Painel Público',
    iconName: 'Insights',
    permission: ['clients', 'suggestions'],
    scope: 'client'
  },
  providers: {
    path: ROUTES.APP_PROVIDERS,
    label: 'Prestadores',
    iconName: 'Engineering',
    permission: 'providers',
    scope: 'global'
  },
  clients: {
    path: ROUTES.APP_CLIENTS,
    label: 'Condomínios',
    iconName: 'Business',
    permission: 'clients',
    scope: 'global'
  },
  users: {
    path: ROUTES.APP_USERS,
    label: 'Usuários',
    iconName: 'People',
    permission: 'users',
    scope: 'global'
  },
  roles: {
    path: ROUTES.APP_ROLES,
    label: 'Papéis',
    iconName: 'Security',
    permission: 'roles',
    scope: 'global'
  },
  resources: {
    path: ROUTES.APP_RESOURCES,
    label: 'Centro de Recursos',
    iconName: 'MenuBook',
    permission: 'demands', // Qualquer usuário logado com acesso a demandas pode ver
    scope: 'global'
  },
  finance: {
    path: ROUTES.APP_FINANCE,
    label: 'Financeiro',
    iconName: 'AccountBalance',
    permission: 'finance',
    scope: 'global'
  },
  config: {
    path: ROUTES.APP_CONFIG,
    label: 'Configurações',
    iconName: 'Settings',
    permission: 'settings',
    scope: 'global'
  }
};

// Função para obter o ícone React a partir do nome
export const getIconElement = (iconName: string): React.ReactElement => {
  const IconComponent = ICONS[iconName];
  if (!IconComponent) {
    return <Assessment />; // fallback
  }
  return <IconComponent />;
};

// Ordem do menu: pedido do usuário (04/09) — os dois grupos de `scope` ficam contíguos (condomínio
// primeiro, geral depois), pra a divisão visual no dropdown (ver App.tsx) fazer sentido. Dentro de
// cada grupo, Configurações continua por último.
const MENU_ORDER: (keyof typeof PAGES_CONFIG)[] = [
  // scope: 'client'
  'demands',
  'reports',
  'checklists',
  'patrimony',
  'qrCodes',
  'recurringDemands',
  // Pedido do usuário (14/09): "Sugestões" e "Caixa de Sugestões" saem daqui — viram a aba
  // "Sugestões" de Painel Público (aceita `clients` OU `suggestions`, ver publicPanel acima).
  'publicPanel',
  // scope: 'global'
  'providers',
  'clients',
  'users',
  'roles',
  'resources',
  'finance',
  'config'
];

// Export all menu items (Configurações sempre última)
export const getAllMenuItems = (): PageConfig[] => {
  return MENU_ORDER.map((key) => PAGES_CONFIG[key]).filter((p): p is PageConfig => Boolean(p));
};

// Get page config by path
export const getPageConfigByPath = (path: string): PageConfig | undefined => {
  return getAllMenuItems().find(page => page.path === path);
};

// Logout config
export const LOGOUT_CONFIG = {
  label: 'Sair',
  iconName: 'Logout'
};

